// 康巴圣德下游应用企业 —— 详情页（图文并茂）
// 由「下游客户热力图」页（/downstream-heat）点击企业节点进入
// 本页按 2221 大屏基准 7680×2160 排布（2223 副本为 3840×2160，比例不同，故重排）
<template>
  <div class="cd-page" :style="rootVars">
      <div class="cd-stage">
      <div class="cd-fit">
          <!-- 未找到企业兜底 -->
          <div v-if="!company" class="cd-empty">
            <p>{{ $C('companyDetail','emptyText','未找到对应企业，请返回重新选择。') }}</p>
            <button class="cd-back-inline" @click="goBack">{{ $C('companyDetail','backText','返回') }}</button>
          </div>

          <template v-else>
            <!-- ① 页头 -->
            <header class="cd-header">
              <div class="cd-header-main">
                <div class="cd-kicker">{{ company.short }} · {{ company.province }}{{ $C('companyDetail','provSuffix','省') }}</div>
                <h1 class="cd-title">{{ company.name }}</h1>
                <div class="cd-chips">
                  <span class="cd-chip cd-chip-prov">{{ company.province }}</span>
                  <span class="cd-chip">{{ company.category }}</span>
                  <span class="cd-chip cd-chip-tag" v-if="company.isDownstream !== false">{{ $C('companyDetail','chipDownstream','酪蛋白酸钠下游应用') }}</span>
                  <span class="cd-chip cd-chip-list" v-if="company.isListed && company.stock">📈 {{ company.stock.market }} {{ company.stock.code }}</span>
                </div>
              </div>
              <button class="cd-back" type="button" @click="goBack">
                <span class="cd-back-arrow">←</span> {{ $C('companyDetail','backText','返回') }}
              </button>
            </header>

            <!-- ② 主体：左图右文 -->
            <div class="cd-body">
              <!-- 左：产品图片 -->
              <aside class="cd-media">
                <div class="cd-media-frame">
                  <img class="cd-media-img" :src="imgSrc" :alt="company.name + ' 产品形态'" />
                  <div class="cd-media-badge">{{ $C('companyDetail','mediaBadge','产品形态') }}</div>
                </div>
                <div class="cd-media-cap">
                  <div class="cd-media-cap-title">{{ $C('companyDetail','mediaCapTitle','产品图片') }}</div>
                  <p class="cd-media-cap-text">{{ company.productDesc }}</p>
                </div>
              </aside>

              <!-- 右：四大板块（图文并茂） -->
              <div class="cd-cards">
                <article class="cd-card cd-card-a">
                  <div class="cd-card-icon">
                    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M8 40V20l8-6v6l8-6v6l8-6v6l8-6v26z" />
                      <path d="M20 40v-8h8v8" />
                      <path d="M8 40h32" />
                    </svg>
                  </div>
                  <div class="cd-card-body">
                    <h2 class="cd-card-title">{{ $C('companyDetail','cardTitleProduction','生产动向') }}</h2>
                    <p class="cd-card-text">{{ company.production }}</p>
                  </div>
                </article>

                <article class="cd-card cd-card-b">
                  <div class="cd-card-icon">
                    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M24 6a13 13 0 0 0-8 23c1.4 1 2 2.4 2 4v3h12v-3c0-1.6.6-3 2-4a13 13 0 0 0-8-23z" />
                      <path d="M19 40h10" />
                      <path d="M24 6v0" />
                    </svg>
                  </div>
                  <div class="cd-card-body">
                    <h2 class="cd-card-title">{{ $C('companyDetail','cardTitlePatent','专利及发展方向') }}</h2>
                    <p class="cd-card-text">{{ company.patent }}</p>
                  </div>
                </article>

                <article class="cd-card cd-card-c">
                  <div class="cd-card-icon">
                    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 38c-4-6-7-12-7-19a13 13 0 0 1 26 0c0 7-3 13-7 19l-6 8z" />
                      <circle cx="24" cy="19" r="5" />
                    </svg>
                  </div>
                  <div class="cd-card-body">
                    <h2 class="cd-card-title">{{ $C('companyDetail','cardTitleFuture','产品未来') }}</h2>
                    <p class="cd-card-text">{{ company.future }}</p>
                  </div>
                </article>

                <article class="cd-card cd-card-d">
                  <div class="cd-card-icon">
                    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M8 38l9-9 7 7 14-16" />
                      <path d="M30 20h8v8" />
                      <path d="M8 42h32" />
                    </svg>
                  </div>
                  <div class="cd-card-body">
                    <h2 class="cd-card-title">{{ $C('companyDetail','cardTitleInvest','投资方向') }}</h2>
                    <p class="cd-card-text">{{ company.invest }}</p>
                  </div>
                </article>
              </div>
            </div>

            <!-- ③ 公司简介 / 上市公司档案 -->
            <section class="cd-intro" v-if="company.intro">
              <h2 class="cd-section-title"><i class="cd-section-bar"></i>{{ $C('companyDetail','introTitle','公司简介') }}<span class="cd-section-sub" v-if="company.isListed">（上市公司档案）</span></h2>
              <div class="cd-intro-body">
                <p class="cd-intro-text">{{ company.intro }}</p>
                <div class="cd-stock" v-if="company.isListed && company.stock">
                  <div class="cd-stock-badge">上市公司</div>
                  <ul class="cd-stock-list">
                    <li><span class="cd-stock-k">证券简称</span><span class="cd-stock-v">{{ company.short }}</span></li>
                    <li><span class="cd-stock-k">股票代码</span><span class="cd-stock-v cd-stock-code">{{ company.stock.code }}</span></li>
                    <li><span class="cd-stock-k">上市市场</span><span class="cd-stock-v">{{ company.stock.market }} · {{ company.stock.exchange }}{{ company.stock.board }}</span></li>
                  </ul>
                </div>
              </div>
            </section>

            <!-- ④ 同行业对标企业（同行） -->
            <section class="cd-peers">
              <h2 class="cd-section-title"><i class="cd-section-bar"></i>{{ $C('companyDetail','peersTitle','同行业对标企业（同行）') }}</h2>
              <div class="cd-peers-wrap" v-if="peers.length">
                <button class="cd-peer" type="button" v-for="p in peers" :key="p.id" @click="goPeer(p.id)">
                  <span class="cd-peer-name">{{ p.short }}</span>
                  <span class="cd-peer-cat">{{ p.category }}</span>
                  <span class="cd-peer-listed" v-if="p.isListed">{{ p.stock.market }}{{ p.stock.code }}</span>
                </button>
              </div>
              <p class="cd-peer-empty" v-else>{{ $C('companyDetail','peersEmpty','本看板数据中暂无同行业直接对标企业') }}</p>
            </section>

            <!-- ⑤ 页脚 -->
            <footer class="cd-footer">
              <span class="cd-footer-logo">{{ company.short }}</span>
              <span class="cd-footer-text">{{ $C('companyDetail','footerSource','数据来源：企业公开工商 / 专利 / 环评 / 项目备案 / 年报 / 行业公开资讯推演，仅供市场调研与数字看板展示') }}</span>
            </footer>
          </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getCompanyById, getPeersOf } from '@/data/companyNetwork'
import { mediaUrl } from '@/cms/config'

export default {
  name: 'companyDetail',
  data() {
    return {}
  },
  computed: {
    company() {
      return getCompanyById(this.$route.params.id)
    },
    peers() {
      return getPeersOf(this.$route.params.id)
    },
    imgSrc() {
      if (!this.company) return ''
      const img = this.company.image
      if (!img) return (process.env.BASE_URL || '/') + 'assets/company-products/placeholder.svg'
      return (process.env.BASE_URL || '/') + img
    },
    rootVars() {
      const bg = this.$C('companyDetail', 'pageBg', '')
      return {
        '--page-bg': bg ? `url("${mediaUrl(bg)}") center/cover no-repeat #04122e` : 'linear-gradient(160deg,#04122e 0%,#0a2248 55%,#061634 100%)',
        '--text-main': '#eaf3ff',
        '--text-sub': '#9fc0e8',
        '--accent': '#31FFFF',
        '--accent2': '#FFC857',
        '--card-bg': 'rgba(13,42,84,0.55)',
        '--card-border': 'rgba(79,163,255,0.32)',
        '--card-shadow': '0 0 28px rgba(12,120,220,0.18)',
        '--heading': '#31FFFF',
        '--radius': '16px',
      }
    },
  },
  mounted() {
    // 页面由外壳 main-layout 统一 --screen-scale 缩放；内容若超出 2160 设计高，由 .cd-page 纵向滚动查看，不再等比缩小（避免文字过小）
  },
  methods: {
    goBack() {
      // 企业详情恒从下游客户热力图进入，统一返回热力图页（与顶部"返回"一致，避免依赖历史乱跳）
      this.$router.replace('/downstream-heat')
    },
    goPeer(id) {
      // 同行企业跳转：进入该企业详情页（与顶部"返回"一致，统一从热力图返回）
      this.$router.push('/company-detail/' + id)
    },
  },
}
</script>

<style lang="less" scoped>
.cd-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--page-bg);
  color: var(--text-main);
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.cd-page::-webkit-scrollbar { width: 18px; }
.cd-page::-webkit-scrollbar-track { background: rgba(8, 26, 56, 0.6); }
.cd-page::-webkit-scrollbar-thumb { background: rgba(49, 255, 255, 0.45); border-radius: 9px; }
.cd-back {
  display: inline-flex;
  align-items: center;
  gap: 20px;
  padding: 24px 48px;
  font-size: 44px;
  color: #eaf3ff;
  background: rgba(10, 40, 84, 0.72);
  border: 1px solid rgba(79, 163, 255, 0.4);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.cd-back:hover { background: rgba(31, 122, 224, 0.85); }
.cd-back-arrow { font-size: 48px; line-height: 1; }

.cd-stage {
  /* 与主页 main-layout 统一：7680 宽设计舞台，由外壳 --screen-scale 等比缩放、左上原点、左上锚定 */
  width: 7680px;
  min-height: 2160px;
  position: relative;
}
.cd-fit {
  width: 7680px;
  padding: 90px 120px 60px;
  box-sizing: border-box;
}

/* 页头 */
.cd-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px 80px;
  margin-bottom: 40px;
  background: linear-gradient(90deg, rgba(13,42,84,0.7), rgba(10,30,64,0.2));
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}
.cd-kicker { font-size: 48px; color: var(--accent2); letter-spacing: 3px; }
.cd-title {
  margin: 16px 0 24px;
  font-size: 88px;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 4px 18px rgba(0, 180, 255, 0.35);
}
.cd-chips { display: flex; gap: 24px; flex-wrap: wrap; }
.cd-chip {
  font-size: 40px;
  color: var(--text-sub);
  padding: 16px 40px;
  border: 1px solid rgba(120, 170, 240, 0.3);
  border-radius: 34px;
  background: rgba(255, 255, 255, 0.04);
}
.cd-chip-prov { color: #04122e; background: var(--accent2); border-color: var(--accent2); font-weight: 700; }
.cd-chip-tag { color: var(--accent); border-color: rgba(49, 255, 255, 0.4); }

/* 主体两栏 */
.cd-body {
  display: flex;
  gap: 80px;
  align-items: stretch;
}
/* 左：产品图片 */
.cd-media {
  width: 2400px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.cd-media-frame {
  position: relative;
  flex: 1;
  min-height: 820px;
  border-radius: var(--radius);
  border: 2px solid var(--card-border);
  background: radial-gradient(circle at 50% 40%, rgba(20,60,120,0.5), rgba(6,20,48,0.6));
  box-shadow: var(--card-shadow);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-media-img {
  max-width: 92%;
  max-height: 92%;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
}
.cd-media-badge {
  position: absolute;
  left: 40px;
  top: 40px;
  font-size: 36px;
  color: #04122e;
  background: var(--accent2);
  padding: 14px 36px;
  border-radius: 34px;
  font-weight: 700;
}
.cd-media-cap {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 32px 44px;
}
.cd-media-cap-title { font-size: 56px; color: var(--accent); font-weight: 700; margin-bottom: 18px; }
.cd-media-cap-text { font-size: 50px; line-height: 1.6; color: var(--text-sub); margin: 0; }

/* 右：四大板块 */
.cd-cards {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: 48px;
}
.cd-card {
  display: flex;
  gap: 40px;
  padding: 40px 48px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  box-shadow: var(--card-shadow);
  transition: transform 0.2s, border-color 0.2s;
}
.cd-card:hover { transform: translateY(-6px); border-color: var(--accent); }
.cd-card-icon {
  flex-shrink: 0;
  width: 130px;
  height: 130px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(49, 255, 255, 0.08);
  border: 1px solid rgba(49, 255, 255, 0.25);
}
.cd-card-icon svg { width: 76px; height: 76px; }
.cd-card-a .cd-card-icon { color: #31FFFF; }
.cd-card-b .cd-card-icon { color: #FFC857; background: rgba(255,200,87,0.08); border-color: rgba(255,200,87,0.3); }
.cd-card-c .cd-card-icon { color: #66BB6A; background: rgba(102,187,106,0.08); border-color: rgba(102,187,106,0.3); }
.cd-card-d .cd-card-icon { color: #FF8A5C; background: rgba(255,138,92,0.08); border-color: rgba(255,138,92,0.3); }
.cd-card-body { flex: 1; min-width: 0; }
.cd-card-title {
  margin: 0 0 26px;
  font-size: 60px;
  font-weight: 800;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 18px;
}
.cd-card-title::before {
  content: '';
  width: 14px;
  height: 44px;
  border-radius: 8px;
  background: currentColor;
}
.cd-card-a .cd-card-title { color: #31FFFF; }
.cd-card-b .cd-card-title { color: #FFC857; }
.cd-card-c .cd-card-title { color: #66BB6A; }
.cd-card-d .cd-card-title { color: #FF8A5C; }
.cd-card-text {
  margin: 0;
  font-size: 52px;
  line-height: 1.6;
  color: var(--text-sub);
}

/* 页脚 */
.cd-footer {
  margin-top: 40px; /* 内容可纵向滚动，不再受 2160 限制 */
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 32px 56px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 40px;
  color: #7fa3cc;
}
.cd-footer-logo { color: var(--accent); font-weight: 700; font-size: 40px; }

/* 头部：上市公司徽标 */
.cd-chip-list {
  color: #04122e;
  background: #FF6B6B;
  border-color: #FF6B6B;
  font-weight: 700;
  animation: cd-list-pulse 2s ease-in-out infinite;
}
@keyframes cd-list-pulse {
  0%, 100% { box-shadow: 0 0 10px rgba(255,107,107,0.5); }
  50% { box-shadow: 0 0 26px rgba(255,107,107,0.95); }
}

/* ③ 公司简介 / 上市公司档案 */
.cd-intro { margin-top: 36px; flex-shrink: 0; }
.cd-section-title {
  display: flex;
  align-items: center;
  gap: 22px;
  margin: 0 0 30px;
  font-size: 62px;
  font-weight: 800;
  color: #fff;
}
.cd-section-bar {
  width: 16px;
  height: 56px;
  border-radius: 8px;
  background: var(--accent);
  box-shadow: 0 0 14px rgba(49,255,255,0.6);
}
.cd-section-sub { font-size: 46px; color: #FF6B6B; font-weight: 700; }
.cd-intro-body {
  display: flex;
  gap: 48px;
  align-items: stretch;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 44px 56px;
}
.cd-intro-text {
  flex: 1;
  margin: 0;
  font-size: 52px;
  line-height: 1.7;
  color: var(--text-main);
  text-align: justify;
}
.cd-stock {
  flex-shrink: 0;
  width: 760px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 36px 40px;
  background: rgba(255, 107, 107, 0.08);
  border: 1px solid rgba(255, 107, 107, 0.4);
  border-radius: 14px;
}
.cd-stock-badge {
  align-self: flex-start;
  font-size: 44px;
  font-weight: 800;
  color: #04122e;
  background: #FF6B6B;
  padding: 12px 40px;
  border-radius: 30px;
}
.cd-stock-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 26px; }
.cd-stock-list li { display: flex; align-items: baseline; gap: 24px; }
.cd-stock-k { flex-shrink: 0; width: 220px; font-size: 42px; color: var(--text-sub); }
.cd-stock-v { font-size: 48px; color: #fff; font-weight: 700; }
.cd-stock-code { color: #FFC857; font-family: 'DIN', 'Arial', sans-serif; letter-spacing: 2px; }

/* ④ 同行业对标企业（同行） */
.cd-peers { margin-top: 36px; flex-shrink: 0; }
.cd-peers-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 44px 56px;
}
.cd-peer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 28px 40px;
  min-width: 360px;
  background: rgba(49, 255, 255, 0.06);
  border: 1px solid rgba(79, 163, 255, 0.35);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-align: left;
}
.cd-peer:hover { background: rgba(49, 255, 255, 0.16); border-color: var(--accent); transform: translateY(-4px); }
.cd-peer-name { font-size: 48px; font-weight: 700; color: #fff; }
.cd-peer-cat { font-size: 36px; color: var(--text-sub); }
.cd-peer-listed {
  font-size: 28px;
  font-weight: 700;
  color: #04122e;
  background: #FF6B6B;
  border-radius: 16px;
  padding: 4px 16px;
}
.cd-peer-empty { margin: 0; padding: 40px 56px; font-size: 40px; color: var(--text-sub); background: var(--card-bg); border: 1px dashed var(--card-border); border-radius: var(--radius); }
.cd-empty {
  min-height: 1600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  font-size: 48px;
  color: var(--text-sub);
}
.cd-back-inline {
  padding: 24px 56px;
  font-size: 36px;
  color: #04122e;
  background: var(--accent);
  border: none;
  border-radius: 48px;
  cursor: pointer;
}
</style>
